import { FC, useEffect, useState } from "react";

import { StopRecordingMenuItem } from "components/menus/RecordingMenuItems";
import { RecordingSessionState } from "components/server-types";
import { t } from "i18n/i18n";
import { Button } from "mds/components/Button";
import { IconText } from "mds/components/IconText";
import { Menu } from "mds/components/Menu";
import { Tooltip } from "mds/components/Tooltip";
import { ChevronDownIcon, RecordingIcon } from "mds/icons";
import { useCapabilities } from "store/capability-hooks";
import { useAppSelector } from "store/index";
import {
  selectActiveRecordingSessionForCurrentCourse,
  selectCurrentClassUserIsInVirtualClass,
} from "store/selectors";
import { toastWarningMessage } from "utils/alerts";

export const RecordingStatusIndicator: FC = () => {
  const { canParticipateInCourse, canStartAndStopRecording } = useCapabilities();
  const isInVirtualClass = useAppSelector(selectCurrentClassUserIsInVirtualClass);

  const activeRecordingSession = useAppSelector(selectActiveRecordingSessionForCurrentCourse);
  const isRecordingSessionStarting =
    activeRecordingSession?.state === RecordingSessionState.STARTING;
  const isRecordingSessionStarted = activeRecordingSession?.state === RecordingSessionState.STARTED;
  const isRecordingSessionStopped = !activeRecordingSession;

  // We need to track this bit of explicit state in order to show the "recording stopped" toast
  // appropriately. The "recording started" toast can be triggered simply by the presence of a
  // RecordingSession in the started state, but the absence of a RecordingSession should only
  // trigger the "recording stopped" toast if it was previously started while the student was
  // present, and not if they joined the class when the RecordingSession was already stopped.
  const [shouldShowStopToast, setShouldShowStopToast] = useState(false);

  useEffect(() => {
    if (canParticipateInCourse && isInVirtualClass) {
      if (isRecordingSessionStarted) {
        toastWarningMessage(t("recording_controls.toast_recording_active"));
        setShouldShowStopToast(true);
      } else if (shouldShowStopToast && isRecordingSessionStopped) {
        toastWarningMessage(t("recording_controls.toast_recording_stopped"));
        setShouldShowStopToast(false);
      }
    }
  }, [
    canParticipateInCourse,
    isInVirtualClass,
    isRecordingSessionStarted,
    isRecordingSessionStopped,
    shouldShowStopToast,
  ]);

  if (!isInVirtualClass) {
    return null;
  }

  if (canStartAndStopRecording && (isRecordingSessionStarting || isRecordingSessionStarted)) {
    const recordingStatus = isRecordingSessionStarting
      ? t("recording_controls.menu_title_starting")
      : t("recording_controls.menu_title_started");

    const MenuButton = (
      <div>
        <Tooltip content={t("recording_controls.menu_tooltip")}>
          <Button aria-label={t("recording_controls.menu_tooltip")} kind="gray" size="s">
            <IconText
              className="gap-2 text-orange"
              iconEnd={<ChevronDownIcon />}
              iconStart={<RecordingIcon />}
              text={recordingStatus}
            />
          </Button>
        </Tooltip>
      </div>
    );

    return (
      <Menu menuButton={MenuButton}>
        <StopRecordingMenuItem recordingSessionId={activeRecordingSession.id} />
      </Menu>
    );
  }

  if (canParticipateInCourse && isRecordingSessionStarted) {
    const recordingStatus = t("recording_controls.menu_title_started");

    return (
      <Tooltip content={t("recording_controls.menu_tooltip")}>
        <IconText className="text-orange" iconStart={<RecordingIcon />} text={recordingStatus} />
      </Tooltip>
    );
  }

  return null;
};
